import React, { useState, useEffect } from "react";
import { getLocalStorage, setLocalStorage, removeLocalStorage } from "../components/utils";
import { message } from "antd";
import { authAPI } from "../components/views/api";

export interface IUser {
  name: string;
  email?: string;
  username?: string;
  avatar_url?: string;
  metadata?: any;
  is_approved?: boolean;
  is_admin?: boolean;
  has_gitlab?: boolean;
  has_pipedrive?: boolean;
  has_microsoft?: boolean;
}

export interface AppContextType {
  user: IUser | null;
  setUser: any;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, name: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  cookie_name: string;
  darkMode: string;
  setDarkMode: any;
}

const cookie_name = "coral_app_cookie_";

// DEV MODE: Set to true to bypass login for local testing
const DEV_MODE_BYPASS_AUTH = false;

export const appContext = React.createContext<AppContextType>(
  {} as AppContextType
);

const Provider = ({ children }: any) => {
  // Start with dark mode to avoid hydration mismatch, then update from localStorage
  const [darkMode, setDarkMode] = useState("dark");
  const [isLoading, setIsLoading] = useState(DEV_MODE_BYPASS_AUTH ? false : true);
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_MODE_BYPASS_AUTH ? true : false);

  // Load dark mode preference from localStorage after mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedValue = getLocalStorage("darkmode", false);
      if (storedValue && storedValue !== darkMode) {
        setDarkMode(storedValue);
      }
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await authAPI.login(email, password);
      const { user: userData, token } = response.data;

      // Store token and user info
      setLocalStorage("auth_token", token.access_token);
      setLocalStorage("user_email", userData.email);
      setLocalStorage("user_id", String(userData.id));

      // Update state
      setUserState({
        name: userData.name || userData.email,
        email: userData.email,
        username: userData.email,
        is_approved: userData.is_approved,
        is_admin: userData.is_admin,
        has_gitlab: userData.has_gitlab,
        has_pipedrive: userData.has_pipedrive,
      });
      setIsAuthenticated(true);

      return true;
    } catch (error: any) {
      console.error("Login failed:", error);
      return false;
    }
  };

  const register = async (email: string, name: string, password: string): Promise<boolean> => {
    try {
      const response = await authAPI.signup(email, password, name);
      const { user: userData, token } = response.data;

      // Store token and user info
      setLocalStorage("auth_token", token.access_token);
      setLocalStorage("user_email", userData.email);
      setLocalStorage("user_id", String(userData.id));

      // Update state
      setUserState({
        name: userData.name || userData.email,
        email: userData.email,
        username: userData.email,
        is_approved: userData.is_approved,
        is_admin: userData.is_admin,
        has_gitlab: userData.has_gitlab,
        has_pipedrive: userData.has_pipedrive,
      });
      setIsAuthenticated(true);

      return true;
    } catch (error: any) {
      console.error("Registration failed:", error);
      return false;
    }
  };

  const logout = async () => {
    try {
      const token = getLocalStorage("auth_token");
      if (token) {
        await authAPI.logout(token);
      }
    } catch (error) {
      // Ignore logout errors
    }

    // Clear all auth data
    removeLocalStorage("auth_token");
    removeLocalStorage("user_email");
    removeLocalStorage("user_id");

    // Reset user state
    setUserState({
      name: "Guest User",
      email: "",
      username: "guestuser",
    });
    setIsAuthenticated(false);

    message.success("Logged out successfully");

    // Redirect to login
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  };

  const updateDarkMode = (darkMode: string) => {
    setDarkMode(darkMode);
    setLocalStorage("darkmode", darkMode, false);
  };

  const setUser = (user: IUser | null) => {
    if (user?.email) {
      setLocalStorage("user_email", user.email, false);
    }
    setUserState(user);
  };

  // Start with null to avoid hydration mismatch (or dev user if DEV_MODE)
  const [userState, setUserState] = useState<IUser | null>(
    DEV_MODE_BYPASS_AUTH
      ? { name: "Dev User", email: "dev@local.test", username: "devuser", is_approved: true, is_admin: true }
      : null
  );

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") {
      return;
    }

    // Skip auth check in dev mode
    if (DEV_MODE_BYPASS_AUTH) {
      console.log("[DEV MODE] Auth bypassed - logged in as Dev User");
      return;
    }

    // Check for GitLab OAuth callback token in URL fragment (#)
    // Fragment is safer than query params — not sent to server in HTTP requests
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const gitlabToken = hashParams.get("gitlab_token");
    if (gitlabToken) {
      setLocalStorage("auth_token", gitlabToken);
      const email = hashParams.get("email") || "";
      const userId = hashParams.get("user_id") || "";
      if (email) setLocalStorage("user_email", email, false);
      if (userId) setLocalStorage("user_id", userId, false);
      // Clean URL
      window.history.replaceState({}, document.title, "/");
    }

    // Verify token on mount
    const token = gitlabToken || getLocalStorage("auth_token");
    if (token) {
      authAPI.getMe(token)
        .then((userData) => {
          setUserState({
            name: userData.name || userData.email,
            email: userData.email,
            username: userData.email,
            is_approved: userData.is_approved,
            is_admin: userData.is_admin,
            has_gitlab: userData.has_gitlab,
            has_pipedrive: userData.has_pipedrive,
          });
          setIsAuthenticated(true);
          setIsLoading(false);
        })
        .catch(() => {
          // Token invalid, clear auth data
          removeLocalStorage("auth_token");
          removeLocalStorage("user_email");
          removeLocalStorage("user_id");
          setUserState({
            name: "Guest User",
            email: "",
            username: "guestuser",
          });
          setIsAuthenticated(false);
          setIsLoading(false);
        });
    } else {
      // No token, set guest user
      setUserState({
        name: "Guest User",
        email: "",
        username: "guestuser",
      });
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  return (
    <appContext.Provider
      value={{
        user: userState,
        setUser,
        login,
        register,
        logout,
        isAuthenticated,
        isLoading,
        cookie_name,
        darkMode,
        setDarkMode: updateDarkMode,
      }}
    >
      {children}
    </appContext.Provider>
  );
};

// Wrapper component that suppresses hydration warnings
const ProviderWrapper = ({ element }: any) => (
  <div suppressHydrationWarning>
    <Provider>{element}</Provider>
  </div>
);

export default ProviderWrapper;
